import { Component, HostListener, Inject, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime, finalize, switchMap } from 'rxjs/operators';
import { Scenario } from 'src/app/care/scenarios/models';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { MetricsService } from 'src/app/shared/services/metrics.service';
import { OptinsService } from './optins.service';
import { Optin, OptinScenario, OptinTypeEnum } from './shared';

@Component({
  selector: 'app-optins',
  templateUrl: './optins.component.html',
  styleUrls: ['./optins.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OptinsComponent implements OnDestroy {
  maxHeight = 500;
  dataSource = [];
  displayedColumns = [
    'name',
    'title',
    'creator_admin_user_id',
    'creation_time',
    'type',
    'scenarios',
    'opted_in_nusers',
    'actions',
  ];

  readonly titleMaxLength = 65;
  form = this.formBuilder.group({
    id: [null],
    name: [this.basicService.translate('optins/new_optin'), Validators.required],
    type: OptinTypeEnum.OPTIN_TYPE_DEFAULT,
    title: ['', [Validators.required, Validators.maxLength(this.titleMaxLength)]],
  });

  OptinTypeEnum = OptinTypeEnum;
  optinScenarios: Scenario[] = [];

  private readonly defaultLabel = this.basicService.translate('admin/users/is_admin');
  private readonly typesTranslations = [
    this.basicService.translate('optins/type/default'),
    this.basicService.translate('optins/type/messengerRecurringNotif'),
    this.basicService.translate('optins/type/instagramRecurringNotif'),
  ];

  optinTypeChoicesList: Choice[] = this.typesTranslations.map((label, index) => ({ id: index, label }));

  private campaignScenarios = [];
  private scenariosListMap: Map<number, OptinScenario[]> = new Map();
  private subscriptions = new Subscription();

  /**
   * Allows to have a full responsive table by taking the available height. The overflow of the table will be visible by using a scroll.
   * @remarks 60 considers the headers (48px) and the margin. As the target is the routed component, the header is ignored.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.maxHeight = Math.max(document.getElementById('right-side-content').clientHeight - 60, 500);
  }

  @ViewChild('formTemplateRef', { static: true }) private formTemplateRef: TemplateRef<any>;
  @ViewChild('scenariosListTemplateRef', { static: true }) private scenariosListTemplateRef: TemplateRef<any>;
  @ViewChild(MatTable) table: MatTable<Optin>;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private optinsService: OptinsService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private sitesService: SitesService,
    metricsService: MetricsService,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('$state') private $state: any
  ) {
    topToolbarService.setButton('optins/create_new', this.addOptin, null, 'add-optin');

    basicService.showSpinner();
    metricsService
      .getScenarios('campaign-scenarios')
      .pipe(
        switchMap((result: any[]) => {
          this.campaignScenarios = result;

          this.scenariosListMap = optinsService.buildOptinScenarios(
            this.campaignScenarios,
            this.sitesService.sitesV2Map
          );
          return this.optinsService.getOptins();
        }),
        finalize(() => this.basicService.hideSpinner())
      )
      .subscribe({
        next: (result: Optin[]) => {
          result.forEach(this.formatOptin);
          this.dataSource = [].concat(result);
          this.onResize();
        },
      });

    this.subscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(value => {
        const { id: formId, name: formName, title: formTitle, type: formtype } = value;
        let error = false;
        const isReccuringNotification = [
          OptinTypeEnum.OPTIN_TYPE_RECURRING_NOTIF_INSTAGRAM,
          OptinTypeEnum.OPTIN_TYPE_RECURRING_NOTIF_MESSENGER,
        ].includes(formtype);

        error = this.dataSource.some(({ name, id }) => (!formId || id !== formId) && name === formName);
        if (error) {
          this.form.get('name').setErrors({ 'unique-name': true });
        }
        if (isReccuringNotification) {
          if (formTitle) {
            const errors = {};
            const uniqueTitleError = this.dataSource.some(
              ({ title, id }) => (!formId || id !== formId) && title === formTitle
            );
            if (uniqueTitleError) {
              errors['unique-title'] = true;
            }
            const uniqueNameTitleTypeError = this.dataSource.some(
              ({ title, id, type, name }) =>
                (!formId || id !== formId) && title === formTitle && name === formName && type === formtype
            );
            if (uniqueNameTitleTypeError) {
              errors['unique-name-title-type'] = true;
            }

            this.form
              .get('title')
              .setErrors(errors['unique-name-title-type'] || errors['unique-title'] ? errors : null);

            console.log(this.form.get('title').errors);
          }
        }
      })
    );
  }

  redirectToScenario(scenario) {
    this.$state.go('auth.alcmeon.fuse.parameters.account.campaign-scenarios-details', {
      siteId: scenario.site.id,
      scenarioId: scenario.id,
      redirectedFromOptins: true,
    });
  }

  private addOptin = () => {
    this.form.get('type').enable();
    const { type } = this.form.value;
    if (type === OptinTypeEnum.OPTIN_TYPE_DEFAULT) {
      this.form.get('title').disable();
    }
    this.subscriptions.add(
      this.form.get('type').valueChanges.subscribe(value => {
        if (value === OptinTypeEnum.OPTIN_TYPE_DEFAULT) {
          this.form.get('title').disable();
        } else {
          this.form.get('title').enable();
        }
      })
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      disableClose: true,
      data: {
        title: this.basicService.translate('optins/create_new'),
        templateRef: this.formTemplateRef,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
        disable: () => {
          return this.form.invalid || this.form.pristine;
        },
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const { type, name, title } = this.form.value;
        this.optinsService.addOptin({ type, name, title }).subscribe({
          next: (optin: Optin) => {
            this.formatOptin(optin);
            this.dataSource.push(optin);
            this.table.renderRows();
            this.basicService.toastSuccess('optins/message_confirmation/created');
          },
          error: () => this.basicService.toastAlert('optins/message_confirmation/unable_to_create'),
        });
      }
      this.resetForm();
    });
  };

  askForEdit(optin: Optin) {
    this.form.patchValue(optin);
    this.form.get('type').disable();
    this.form.get('title').disable();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      disableClose: true,
      data: {
        title: this.basicService.translate('optins/edit'),
        templateRef: this.formTemplateRef,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.optinsService.saveOptin(this.form.value).subscribe({
          next: (editedOptin: Optin) => {
            this.formatOptin(editedOptin);
            const index = this.dataSource.findIndex(({ id }) => id === editedOptin.id);
            this.dataSource[index] = { ...editedOptin };
            this.table.renderRows();
            this.basicService.toastSuccess('optins/message_confirmation/edited');
          },
          error: () => this.basicService.toastAlert('optins/message_confirmation/unable_to_edit'),
        });
      }
      this.form.get('type').enable();
      this.form.get('title').enable();
      this.resetForm();
    });
  }

  private resetForm() {
    this.form.patchValue({
      id: null,
      name: this.basicService.translate('optins/new_optin'),
      type: OptinTypeEnum.OPTIN_TYPE_DEFAULT,
      title: '',
    });
  }

  askForDelete(element: Optin) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('optins/confirm_optin_deletion'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.optinsService.deleteOptin(element.id).subscribe({
          next: () => {
            const index = this.dataSource.indexOf(element);
            this.dataSource.splice(index, 1);
            this.table.renderRows();
            this.basicService.toastSuccess('optins/message_confirmation/deleted');
          },
          error: () => this.basicService.toastAlert('optins/message_confirmation/unable_to_delete'),
        });
      }
    });
  }

  private formatOptin = (optin: Optin) => {
    optin.email = this.alcmeonContextService.getColleague(optin.creator_admin_user_id)?.email ?? this.defaultLabel;
    optin.typeLabel = this.typesTranslations[optin.type];
    optin.scenariosList = this.scenariosListMap.get(optin.id) ?? [];
    optin.scenariosList = optin.scenariosList.sort((a, b) =>
      moment(b.last_modification_date).isSameOrAfter(a.last_modification_date) ? 1 : -1
    );
    optin.scenariosPreview = [].concat(optin.scenariosList || []).splice(0, 2);
    const optinListMoreNumber = optin.scenariosList.length - 2;
    optin.listMoreLabel =
      optinListMoreNumber > 0
        ? `+ ${optinListMoreNumber} ${this.basicService.translate(optinListMoreNumber === 1 ? 'other' : 'others')}`
        : '';
  };

  trackByFn(_, item: { id: number }) {
    return item.id;
  }

  showMoreScenarios(scenarios: Scenario[]) {
    this.optinScenarios = scenarios;
    this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        title: this.basicService.translate('optins/scenario_names'),
        templateRef: this.scenariosListTemplateRef,
        confirmBtnText: this.basicService.translate('close'),
      },
    });
  }

  ngOnDestroy(): void {
    this.topToolbarService.clearButtons();
    this.subscriptions?.unsubscribe();
  }
}
